import { useState, useRef, useCallback } from 'react';
import { X, Upload, Camera, Loader2, CheckCircle, User } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface AvatarUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentAvatarUrl?: string | null;
    onAvatarUpdated: (newUrl: string) => void;
}

export function AvatarUploadModal({ isOpen, onClose, currentAvatarUrl, onAvatarUpdated }: AvatarUploadModalProps) {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const dragStart = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setError('Image must be under 5MB');
            return;
        }

        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        setCropOffset({ x: 0, y: 0 });
        setError('');
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (!imageRef.current) return;
        setIsDragging(true);
        dragStart.current = {
            x: e.clientX,
            y: e.clientY,
            offsetX: cropOffset.x,
            offsetY: cropOffset.y,
        };
    };

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isDragging) return;
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        setCropOffset({
            x: dragStart.current.offsetX + dx,
            y: dragStart.current.offsetY + dy,
        });
    }, [isDragging]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Attach global mouse handlers for dragging
    useState(() => {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    });

    const handleUpload = async () => {
        if (!selectedFile) return;

        setUploading(true);
        setError('');

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('You must be logged in');

            // Create canvas for cropping to circle
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx || !imageRef.current) throw new Error('Canvas not supported');

            const size = 256; // Output size
            canvas.width = size;
            canvas.height = size;

            // Draw circular clip
            ctx.beginPath();
            ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
            ctx.clip();

            // Draw image with offset
            const img = imageRef.current;
            const scale = Math.max(size / img.naturalWidth, size / img.naturalHeight);
            const scaledWidth = img.naturalWidth * scale;
            const scaledHeight = img.naturalHeight * scale;
            const offsetX = (size - scaledWidth) / 2 + cropOffset.x * scale;
            const offsetY = (size - scaledHeight) / 2 + cropOffset.y * scale;

            ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);

            // Convert to blob
            const blob = await new Promise<Blob>((resolve, reject) => {
                canvas.toBlob(b => b ? resolve(b) : reject(new Error('Failed to create blob')), 'image/webp', 0.9);
            });

            // Upload to Supabase Storage
            const fileName = `${user.id}/avatar.webp`;
            const { error: uploadError } = await supabase.storage
                .from('avatars')
                .upload(fileName, blob, { upsert: true, contentType: 'image/webp' });

            if (uploadError) throw uploadError;

            // Get public URL
            const { data: { publicUrl } } = supabase.storage
                .from('avatars')
                .getPublicUrl(fileName);

            // Update user metadata
            const { error: updateError } = await supabase.auth.updateUser({
                data: { avatar_url: `${publicUrl}?t=${Date.now()}` }
            });

            if (updateError) throw updateError;

            setSuccess(true);
            onAvatarUpdated(`${publicUrl}?t=${Date.now()}`);

        } catch (err: any) {
            console.error('Avatar upload error:', err);
            setError(err.message || 'Failed to upload avatar');
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
        setError('');
        setSuccess(false);
        setCropOffset({ x: 0, y: 0 });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
            <div className="bg-slate-900 border border-white/10 rounded-xl w-full max-w-md overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                            <Camera className="size-5 text-purple-400" />
                        </div>
                        <h2 className="text-lg font-bold text-white">Profile Picture</h2>
                    </div>
                    <button onClick={handleClose} className="text-white/40 hover:text-white/60">
                        <X className="size-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {success ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="size-8 text-green-400" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">Avatar Updated!</h3>
                            <button
                                onClick={handleClose}
                                className="mt-4 px-6 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Current/Preview Avatar */}
                            <div className="flex justify-center">
                                <div
                                    className="w-40 h-40 rounded-full overflow-hidden border-4 border-purple-500/30 bg-slate-800 flex items-center justify-center cursor-move"
                                    onMouseDown={handleMouseDown}
                                >
                                    {previewUrl ? (
                                        <img
                                            ref={imageRef}
                                            src={previewUrl}
                                            alt="Preview"
                                            className="min-w-full min-h-full object-cover pointer-events-none"
                                            style={{
                                                transform: `translate(${cropOffset.x}px, ${cropOffset.y}px)`,
                                            }}
                                            draggable={false}
                                        />
                                    ) : currentAvatarUrl ? (
                                        <img src={currentAvatarUrl} alt="Current avatar" className="w-full h-full object-cover" />
                                    ) : (
                                        <User className="size-16 text-white/20" />
                                    )}
                                </div>
                            </div>

                            {previewUrl && (
                                <p className="text-center text-xs text-white/40">Drag image to adjust position</p>
                            )}

                            {/* File Input */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileSelect}
                                className="hidden"
                            />

                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full py-3 bg-white/5 border border-white/10 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-colors flex items-center justify-center gap-2"
                            >
                                <Upload className="size-5" />
                                {selectedFile ? 'Choose Different Image' : 'Select Image'}
                            </button>

                            {error && (
                                <p className="text-sm text-red-400 text-center">{error}</p>
                            )}

                            {/* Upload Button */}
                            {selectedFile && (
                                <button
                                    onClick={handleUpload}
                                    disabled={uploading}
                                    className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:opacity-50 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2"
                                >
                                    {uploading ? (
                                        <Loader2 className="size-5 animate-spin" />
                                    ) : (
                                        <>
                                            <Camera className="size-5" />
                                            Save Avatar
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
